import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination2 } from "../../../Elements/Pagination";
import DataTable from "../../../Elements/DataTable";
import EmptyDataAlert from "../../../Elements/EmptyDataAlert";
import { DashboardLoading } from "../../../Elements/ServeLoading";
import Popup from "reactjs-popup";
import * as XLSX from "xlsx";
import { usePagination, useTable } from "react-table";
import cookies from "../../../../../../CommonFunction/cookies";
import { KYCDefaultUser } from "../../../../../../API/Bank/Dashboard";
import { FaDownload, FaSpinner } from "react-icons/fa";

const KYCDefault = ({ isOpen, onClose, bankID, branchID, role }) => {
  const jwtToken = cookies.get("jwt");
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("user");
  const [filterValue, setFilterValue] = useState([]);
  const [showList, setShowList] = useState(false);
  const [isExport, setIsExport] = useState(true);
  const [isExportLoading, setExportLoading] = useState();
  const [error, setError] = useState(false);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchData = useCallback(
    async (page = 1, query = searchQuery) => {
      setLoading(true);
      try {
        const data = await KYCDefaultUser(!isExport, jwtToken, page, query, bankID, branchID, role);
        setLoanData(data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [jwtToken]
  );

  useEffect(() => {
    if (loanData) {
      const filteredData =
        selectedFilter === "user"
          ? loanData?.userKycDefaulter?.docs || []
          : loanData?.organizationKycDefaulter?.docs || [];

      setFilterValue(filteredData);
      setPagination({
        totalDocs:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.totalDocs
            : loanData?.organizationKycDefaulter?.totalDocs,
        totalPages:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.totalPages
            : loanData?.organizationKycDefaulter?.totalPages,
        page:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.page
            : loanData?.organizationKycDefaulter?.page,
        limit:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.limit
            : loanData?.organizationKycDefaulter?.limit,
        hasNextPage:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.hasNextPage
            : loanData?.organizationKycDefaulter?.hasNextPage,
        hasPrevPage:
          selectedFilter === "user"
            ? loanData?.userKycDefaulter?.hasPrevPage
            : loanData?.organizationKycDefaulter?.hasPrevPage,
      });
    }
  }, [loanData, selectedFilter]);

  const columns = useMemo(
    () => [
      {
        Header: "Borrower Name",
        accessor: "",
        Cell: ({ row }) => (
          <div className="my-1 px-2">
            <p>
              {row?.original?.name || ""} {row?.original?.first_name || ""}{" "}
              {row?.original?.middle_name || ""}{" "}
              {row?.original?.last_name || ""}
            </p>
          </div>
        ),
      },
      {
        Header: "UCID",
        accessor: "system_id",
      },
      {
        Header: "Contact no.",
        accessor: "phone_number",
      },
      {
        Header: "Email",
        accessor: "email",
      },
    ],
    []
  );

  const exportToExcel = async () => {
    setExportLoading(true);
    const data = await KYCDefaultUser(isExport, jwtToken, "", "", bankID, role);
    setExportLoading(false);

    const userDocs = data?.userKycDefaulter?.docs || [];
    const orgDocs = data?.organizationKycDefaulter?.docs || [];

    const formatedData = [
      ...userDocs?.map((user) => ({
        Type: "User",
        Name: `${user.first_name || ""} ${user.middle_name || ""} ${
          user.last_name || ""
        }`.trim(),
        SystemID: user.system_id,
        Email: user.email,
        Phone: user.phone_number,
        Role: user.role,
      })),
      ...orgDocs?.map((org) => ({
        Type: "Organization",
        Name: org.name,
        SystemID: org.system_id,
        Email: org.email,
        Phone: org.phone_number,
        Role: org.role,
      })),
    ];

    const ws = XLSX.utils.json_to_sheet(formatedData);
    
    const columnWidths = [
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 10 },
    ];
    ws["!cols"] = columnWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "KYC Defaulters");
    XLSX.writeFile(wb, "KYC_Defaulters.xlsx");
  };

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data: filterValue,
        initialState: { pageIndex: 0 },
        manualPagination: true,
      },
      usePagination
    );

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    fetchData(1, searchQuery);
  };

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) fetchData(pagination.page + 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  const previousPage = useCallback(() => {
    if (pagination.hasPrevPage) fetchData(pagination.page - 1, searchQuery);
  }, [fetchData, searchQuery, pagination]);

  return (
    <>
      <Popup
        open={isOpen}
        modal
        closeOnDocumentClick={false}
        className="custom-popup"
      >
        <div className="popup-style popup-content">
          <div className="bg-bgclr8 md:w-[950px] w-[400px]  mx-auto h-[520px] rounded-[14px]">
            <div className="flex justify-between font-avenir text-bgclr px-10 pt-5">
              <h1 className="text-[20px] font-[600] ">KYC Defaulters </h1>
              <div className="flex gap-4">
                <button
                  type="submit"
                  onClick={() => {
                    exportToExcel();
                  }}
                  disabled={loading || isExportLoading}
                  className="flex gap-2 items-center py-1 px-7  bg-bgclr rounded-md text-txtclr"
                >
                  {isExportLoading ? (
                    <FaSpinner size={18} className="animate-spin text-txtclr" />
                  ) : (
                    <FaDownload size={18} />
                  )}{" "}
                  Download Report
                </button>
                <button
                  type="submit"
                  onClick={() => {
                    onClose();
                  }}
                  // disabled={loading || isExportLoading}
                  className="font-bold py-1 px-3 rounded-md bg-red-700 text-txtclr"
                >
                  X
                </button>
              </div>
            </div>
            <div>
              {!showLoanDetails ? (
                <div>
                  <div className="searchBar-style-dashboard">
                    <select
                      value={selectedFilter}
                      onChange={(e) => {
                        setSelectedFilter(e.target.value);
                        setShowList(false);
                      }}
                      className="border border-[#EBEBEB] text-gray-400 rounded-md lg:pl-2 lg:pr-5 py-3 lg:mr-2"
                    >
                      <option value="user">Individual</option>
                      <option value="entity">Organization</option>
                    </select>
                    <input
                      type="search"
                      placeholder="Search by UCID"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="searchBar-input-dashboard"
                    />
                    <button
                      onClick={() => handleSearch()}
                      className="searchBar-btn-dashboard"
                    >
                      Search
                    </button>
                  </div>
                  {loading ? (
                    <div className="flex justify-center h-[360px] items-center">
                      <DashboardLoading />
                    </div>
                  ) : (
                    <>
                      {loanData.length === 0 || error ? (
                        <div className="h-screen">
                          <EmptyDataAlert
                            title={
                              error
                                ? "No Loans Found"
                                : "Search Loan by Loan number"
                            }
                          />
                        </div>
                      ) : (
                        <div className="mt-2">
                          <div>
                            <div className="pt-3 w-[900px] mx-auto overflow-x-auto h-[335px] overflow-y-auto ">
                              <DataTable
                                getTableProps={getTableProps}
                                headerGroups={headerGroups}
                                getTableBodyProps={getTableBodyProps}
                                page={page}
                                prepareRow={prepareRow}
                                selectedLoanId={""}
                              />
                            </div>
                            <Pagination2
                              previousPage={previousPage}
                              nextPage={nextPage}
                              canPreviousPage={pagination.hasPrevPage}
                              canNextPage={pagination.hasNextPage}
                              pagination={{
                                page: pagination.page,
                                totalPages: pagination.totalPages,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default KYCDefault;
